'use es6';

import { useEffect, useMemo, useState, useRef, useContext, useCallback } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import I18n from 'I18n';
import { DatasetInsightParams } from 'reporting-data/tsTypes/datasetInsightTypes';
import { getReportDashboardId, getReportDashboardInfo, getReportId, getDatasetInsightParams } from 'reporting-data/report/reportGetters';
import { getReportDefinition } from 'reporting-data/report/reportGetters';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import SnowflakeReportError from 'reporting-snowflake/relational/components/SnowflakeReportError';
import { handleInlineTableSorting } from 'reporting-snowflake/relational/modify/sorts/report-sort-utils';
import { ReportWithOptions } from 'reporting-snowflake/relational/resolve/resolve-records';
import { RelationalReport } from 'reporting-snowflake/relational/schema/report-records';
import { VisualTypes } from 'reporting-snowflake/relational/schema/visual-records';
import { findViolatedCompletionCriteriaWithMeta, findViolatedUnsupportedCriteriaWithMeta, isValidWithMeta } from 'reporting-snowflake/relational/validate/validation-utils';
import NoData from 'reporting-ui-components/components/shared/NoData';
import useReportingIngestionInteraction from 'reporting-ui-components/components/shared/hooks/useReportingIngestionInteraction';
import { UnifiedVizValidationContext } from 'reporting-ui-components/components/PlatformReport/v2/UnifiedVizValidationContextProvider';
import useSnowflakeRaaS from 'reporting-ui-components/components/snowflake/useSnowflakeRaaS';
import { ReportType } from 'reporting-ui-components/types/ReportTypes';
import Visualization from 'reporting-visualizations/Visualization';
import { Overlay, OverlayContent } from 'foundations-components/transitional/components/Overlay';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import useMeta from './internal/useMeta';
import { useReportOptions } from './report-options';
import SnowflakePaginator from './SnowflakePaginator';
import ResponsiveReportContainer from '../shared/ResponsiveReportContainer';
import { usePaginationTracking } from '../../monitoring/PaginationMetricsHook';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LoadingOverlay = ({
  loadUse
}) => {
  switch (loadUse) {
    case 'refresh':
      return /*#__PURE__*/_jsx("div", {
        style: {
          zIndex: 0,
          height: 400
        },
        children: /*#__PURE__*/_jsx(Overlay, {
          contextual: true,
          use: "light",
          width: 250,
          children: /*#__PURE__*/_jsx(Flex, {
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-around",
            minBlockSize: "100%",
            children: /*#__PURE__*/_jsx(OverlayContent, {
              children: /*#__PURE__*/_jsxs(Flex, {
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-around",
                inlineSize: "100%",
                children: [/*#__PURE__*/_jsx(LoadingSpinner, {}), I18n.text('reporting-snowflake.refreshing')]
              })
            })
          })
        })
      });
    case 'overlay':
    default:
      return /*#__PURE__*/_jsx(LoadingSpinner, {
        layout: "centered"
      });
  }
};
const visualizationIsEmpty = visualization => {
  if (!visualization) {
    return true;
  }
  const primaryDataset = visualization.data.get('primary');
  return (primaryDataset.data || List()).size === 0;
};
const sortInQuery = (sort, reportDefinition) => reportDefinition.columns.has(sort.column);
const renderEmailExportView = (visualization, debug) => /*#__PURE__*/_jsx(Visualization, {
  visualization: visualization.setIn(['style', 'bottomLegend'], true).setIn(['style', 'height'], 600),
  debug: debug
});
const generateRenderedReport = ({
  loadUse,
  reportDefinition,
  setReportOptions,
  report,
  debug,
  visualizationDebug,
  reportOptions,
  userInfo,
  isMobile,
  noDataRenderer,
  errorRenderer,
  meta,
  vizContainerStyleProps
}) => ({
  loading,
  error,
  visualization,
  dataset
}) => {
  const overlay = loading && /*#__PURE__*/_jsx(LoadingOverlay, {
    loadUse: loadUse
  });
  const {
    gates
  } = userInfo;
  if (loading) {
    return /*#__PURE__*/_jsx(ResponsiveReportContainer, {
      children: overlay
    });
  }
  if (!isValidWithMeta(reportDefinition, meta, userInfo)) {
    const violatedCriteria = findViolatedCompletionCriteriaWithMeta(reportDefinition, meta, userInfo) || findViolatedUnsupportedCriteriaWithMeta(reportDefinition, meta, userInfo);
    const getViolatedCriteriaMessage = () => {
      return {
        header: I18n.text(`reporting-snowflake.violated-constraints-dashboard.${violatedCriteria.get('name')}`).header,
        message: I18n.text(`reporting-snowflake.violated-constraints-dashboard.${violatedCriteria.get('name')}`).message || I18n.text(`reporting-snowflake.violated-constraints-dashboard.${violatedCriteria.get('name')}`)
      };
    };
    const errorMessage = violatedCriteria ? getViolatedCriteriaMessage() : I18n.text('reporting-snowflake.report-errors.generic');
    return /*#__PURE__*/_jsxs(ResponsiveReportContainer, {
      children: [/*#__PURE__*/_jsx(EmptyState, {
        layout: "vertical",
        primaryContent: /*#__PURE__*/_jsxs(View, {
          padding: "200",
          marginBlock: "100",
          marginInline: "auto",
          as: "div",
          children: [/*#__PURE__*/_jsx("h4", {
            children: errorMessage.header
          }), /*#__PURE__*/_jsx("p", {
            children: errorMessage.message
          })]
        })
      }), overlay]
    });
  }
  if (error) {
    if (errorRenderer) {
      return errorRenderer(error, report);
    }
    return /*#__PURE__*/_jsxs(ResponsiveReportContainer, {
      children: [/*#__PURE__*/_jsx(SnowflakeReportError, {
        error: error
      }), overlay]
    });
  }
  if (!visualization) {
    return /*#__PURE__*/_jsx(ResponsiveReportContainer, {
      children: overlay
    });
  }
  if (visualizationIsEmpty(visualization)) {
    if (typeof noDataRenderer === 'function') {
      return noDataRenderer();
    }
    return /*#__PURE__*/_jsxs(ResponsiveReportContainer, {
      children: [/*#__PURE__*/_jsx(NoData, {
        isMobile: isMobile,
        visualizationDebug: visualizationDebug
      }), overlay]
    });
  }
  const handlePageChange = nextPage => {
    setReportOptions(state => {
      const newOffset = state.limit * (nextPage - 1);
      return state.set('offset', newOffset);
    });
  };
  const handleLimitChange = newLimit => {
    setReportOptions(state => {
      return state.set('limit', newLimit);
    });
  };
  const isTableVisual = reportDefinition.visual.type === VisualTypes.TABLE;
  const isXYVisual = !(isTableVisual || reportDefinition.visual.type === VisualTypes.PIVOT_TABLE || reportDefinition.visual.type === VisualTypes.TEXT || reportDefinition.visual.type === VisualTypes.PIE || reportDefinition.visual.type === VisualTypes.DONUT || reportDefinition.visual.type === VisualTypes.GAUGE);
  const showEmailExportView = isXYVisual && report.getIn(['displayParams', 'onlyLegend']) === true;
  const renderedVisualization = showEmailExportView ? renderEmailExportView(visualization, debug) : isMobile ? /*#__PURE__*/_jsx(Visualization, {
    visualization: visualization,
    debug: visualizationDebug,
    gates: gates,
    vizContainerStyleProps: vizContainerStyleProps,
    blockSize: "100vh"
  }) : /*#__PURE__*/_jsx(Visualization, {
    visualization: visualization,
    debug: visualizationDebug,
    gates: gates,
    vizContainerStyleProps: vizContainerStyleProps
  });
  return /*#__PURE__*/_jsxs(ResponsiveReportContainer, {
    isXYVisual: isXYVisual,
    isMobile: isMobile,
    children: [renderedVisualization, isTableVisual && /*#__PURE__*/_jsx(SnowflakePaginator, {
      reportOptions: reportOptions,
      dataset: dataset,
      isMobile: isMobile,
      onLimitChange: handleLimitChange,
      onPageChange: handlePageChange
    }), overlay]
  });
};
const RaaSResolve = ({
  reportWithOptions,
  resolveParameters,
  RenderedReport,
  debug,
  mobileReportingOptions
}) => {
  const userInfo = useContext(UserInfoContext);
  const {
    gates
  } = userInfo;
  const {
    visualization,
    dataset,
    datasets,
    loading,
    error
  } = useSnowflakeRaaS(reportWithOptions, Object.assign({}, resolveParameters, {
    mobileReportingOptions
  }), gates);
  const {
    setLegacyVisualization
  } = useContext(UnifiedVizValidationContext);
  useEffect(() => {
    if (!loading && visualization) {
      try {
        setLegacyVisualization(visualization);
      } catch (e) {
        console.error('Error setting legacy visualization', e);

        // TODO: Log error with ReportingLogger
      }
    }
  }, [loading, setLegacyVisualization, visualization]);
  useEffect(() => {
    if (debug !== null && debug !== void 0 && debug.set) {
      debug.set('Processed Dataset', dataset);
      debug.set('Processed Datasets', datasets);
      debug.set('Dataset Error', error === null || error === void 0 ? void 0 : error.message);
      debug.set('Snowflake Visualization', visualization);
    }
  }, [visualization, dataset, datasets, error, debug]);
  return /*#__PURE__*/_jsx(RenderedReport, {
    visualization: visualization,
    dataset: dataset,
    loading: loading,
    error: error
  });
};
const SnowflakeReport = ({
  report,
  initialReportOptions,
  onResolveStart = emptyFunction,
  onReportResolve = emptyFunction,
  onDrilldown,
  onError = emptyFunction,
  loadUse = 'basic',
  debug,
  allowSort = true,
  flushSortsOnDrilldown = false,
  isMobile = false,
  noDataRenderer,
  errorRenderer,
  onReportChange,
  visualizationDebug,
  compact,
  mobileReportingOptions,
  vizContainerStyleProps
}) => {
  const reportDefinition = useMemo(() => {
    const rDefinition = getReportDefinition(report);
    return RelationalReport(rDefinition);
  }, [report]);
  const userInfo = useContext(UserInfoContext);
  const insightParams = getDatasetInsightParams(report);
  const insightParamsRecord = useMemo(() => insightParams ? DatasetInsightParams(insightParams) : undefined, [insightParams]);
  const reportIngestionOptions = useMemo(() => ({
    reportId: report && report.get('id')
  }), [report]);
  const ingestionInteraction = useReportingIngestionInteraction(report, reportIngestionOptions);
  const snowflakeOnSettled = useCallback((...args) => {
    onReportResolve(...args);
  }, [onReportResolve]);
  const snowflakeOnSuccess = useCallback(() => {
    ingestionInteraction.logSuccess();
  }, [ingestionInteraction]);
  const snowflakeOnError = useCallback(error => {
    ingestionInteraction.logError(error);
    onError(error);
  }, [ingestionInteraction, onError]);
  const [inlineTableSorts, setInlineTableSorts] = useState(List());
  const reportId = inlineTableSorts.length > 0 ? null : report.get('id');
  const [reportOptions, setReportOptions] = useReportOptions(initialReportOptions, reportId, reportDefinition);
  const maxPageNumber = useRef(1);
  const currentRowLimit = useRef(0);

  //This contains a hook used for using metrics to track pagination use.
  usePaginationTracking(report, maxPageNumber, currentRowLimit);

  //This is used to track page changes & row limit in a paginated report
  useEffect(() => {
    if (reportOptions) {
      const {
        limit,
        offset
      } = reportOptions;
      const page = Math.floor(offset / limit) + 1;
      if (page > maxPageNumber.current) {
        maxPageNumber.current = page;
      }
      if (limit !== currentRowLimit.current) {
        currentRowLimit.current = limit;
      }
    }
  }, [reportOptions]);
  useEffect(() => {
    const inlineSortsInQuery = inlineTableSorts.filter(sort => sortInQuery(sort, reportDefinition));
    if (inlineSortsInQuery.size !== inlineTableSorts.size) {
      setInlineTableSorts(inlineSortsInQuery);
    }
  }, [inlineTableSorts, reportDefinition]);
  const shouldSetTableSorts = useMemo(() => inlineTableSorts.size > 0 ? !inlineTableSorts.equals(reportDefinition.sorts) : false, [inlineTableSorts, reportDefinition.sorts]);
  const reportWithOptions = useMemo(() => ReportWithOptions(Object.assign({
    reportDefinition: shouldSetTableSorts ? reportDefinition.set('sorts', inlineTableSorts) : reportDefinition,
    reportOptions,
    reportId,
    dashboardId: getReportDashboardId(report),
    reportDashboardInfo: getReportDashboardInfo(report),
    id: getReportId(report)
  }, insightParamsRecord ? {
    insightParams: insightParamsRecord
  } : null)), [shouldSetTableSorts, reportDefinition, inlineTableSorts, reportOptions, reportId, report, insightParamsRecord]);
  const reportMetaState = useMeta(reportWithOptions.reportDefinition);
  if (debug && debug.set) {
    debug.set('Report w/ Options', reportWithOptions);
  }
  const [updatedFullReport, setUpdatedFullReport] = useState(report);
  useEffect(() => {
    if (shouldSetTableSorts) {
      const updatedReport = report && report.setIn(['reportDefinition', 'sorts'], inlineTableSorts);
      if (onReportChange && updatedReport && !updatedReport.equals(updatedFullReport)) {
        setUpdatedFullReport(updatedReport);
        onReportChange(updatedReport);
      }
    }
  }, [inlineTableSorts, onReportChange, report, shouldSetTableSorts, updatedFullReport]);
  const RenderedReport = generateRenderedReport({
    loadUse,
    reportDefinition: reportWithOptions.reportDefinition,
    setReportOptions,
    report,
    debug,
    reportOptions,
    userInfo,
    isMobile,
    noDataRenderer,
    errorRenderer,
    meta: reportMetaState.data,
    visualizationDebug,
    vizContainerStyleProps
  });
  if (reportMetaState.loading || reportMetaState.error) {
    return /*#__PURE__*/_jsx(RenderedReport, {
      loading: reportMetaState.loading,
      error: reportMetaState.error
    });
  }
  const resolveParameters = {
    reportId,
    onStart: onResolveStart,
    onSettled: snowflakeOnSettled,
    onError: snowflakeOnError,
    onQuerySuccess: snowflakeOnSuccess,
    onDrilldown: onDrilldown ? (...args) => {
      if (flushSortsOnDrilldown) {
        setInlineTableSorts(List());
      }
      return onDrilldown(...args);
    } : undefined,
    onSort: allowSort ? alias => setInlineTableSorts(handleInlineTableSorting(alias, reportDefinition, inlineTableSorts, reportMetaState.data)) : undefined,
    isMobile,
    compact
  };
  return /*#__PURE__*/_jsx(RaaSResolve, {
    reportWithOptions: reportWithOptions,
    resolveParameters: resolveParameters,
    RenderedReport: RenderedReport,
    debug: debug,
    mobileReportingOptions: mobileReportingOptions
  });
};
SnowflakeReport.propTypes = {
  report: ReportType.isRequired,
  initialReportOptions: PropTypes.object,
  onResolveStart: PropTypes.func,
  onReportResolve: PropTypes.func,
  onError: PropTypes.func,
  onDrilldown: PropTypes.func,
  loadUse: PropTypes.oneOf(['basic', 'refresh']),
  debug: PropTypes.object,
  isMobile: PropTypes.bool,
  allowSort: PropTypes.bool,
  flushSortsOnDrilldown: PropTypes.bool,
  noDataRenderer: PropTypes.func,
  errorRenderer: PropTypes.func,
  onReportChange: PropTypes.func,
  visualizationDebug: PropTypes.object,
  compact: PropTypes.bool
};
export const __TESTABLE__ = {
  RaaSResolve
};
export default SnowflakeReport;